import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addRoutingGroup'
  | 'bulkAddRoutingGroups'
  | 'getRoutingGroup'
  | 'getRoutingGroups'
  | 'updateRoutingGroup';

export const useRoutingGroups = () => {
  const [action, setAction] = useState<Action>('');
  const [bulkRoutingGroups, setBulkRoutingGroups] =
    useState<BulkRoutingGroup>();
  const [routingGroup, setRoutingGroup] = useState<RoutingGroup | null>(null);
  const [routingGroups, setRoutingGroups] = useState<RoutingGroup[]>([]);
  const { loading, makeRequest, response } = useFetch();
  const {
    id: operatorId,
    routingGroups: operatorRoutingGroups,
    updateOperatorState,
  } = useOperator();
  const { sortAsc } = useSort<{ description: string }>('description');
  const BASE_URL = `${BASE_URL_MAP['Residential']}/${operatorId}/RoutingGroups`;

  useEffect(() => {
    resetState();
  }, [operatorId]);

  useEffect(() => {
    if (response && !loading) {
      if (action === 'bulkAddRoutingGroups') {
        const bulkResponse = mapBulkResponse(response);
        setBulkRoutingGroups(bulkResponse);
        updateOperatorState('routingGroups', [
          ...(operatorRoutingGroups ?? [])
            .concat(bulkResponse.success)
            .sort(sortAsc),
        ]);
      } else if (action === 'updateRoutingGroup') {
        const mappedRoutingGroup = mapRoutingGroup(response);
        updateOperatorState(
          'routingGroups',
          [
            mappedRoutingGroup,
            ...(operatorRoutingGroups ?? []).filter(
              (routingGroup) => routingGroup.id !== mappedRoutingGroup.id
            ),
          ].sort(sortAsc)
        );
      } else if (action === 'addRoutingGroup') {
        const mappedRoutingGroup = mapRoutingGroup(response);

        updateOperatorState(
          'routingGroups',
          [mappedRoutingGroup, ...(operatorRoutingGroups ?? [])].sort(sortAsc)
        );
      } else if (action === 'getRoutingGroup')
        setRoutingGroup(mapRoutingGroup(response));
      else if (action === 'getRoutingGroups')
        setRoutingGroups(
          response.map((routingGroup: ApiRoutingGroup) =>
            mapRoutingGroup(routingGroup)
          )
        );
    }
    // eslint-disable-next-line
  }, [loading, response]);

  const addRoutingGroup = (routingGroup: CreateRoutingGroup) => {
    setAction('addRoutingGroup');
    const body = mapApiRoutingGroup(routingGroup);

    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const bulkAddRoutingGroups = (routingGroup: CreateRoutingGroup[]) => {
    setAction('bulkAddRoutingGroups');
    const body = routingGroup.map((routingGroup) =>
      mapApiRoutingGroup(routingGroup)
    );

    makeRequest(`${BASE_URL}/BulkAdd`, 'post', sanitizeValue(body));
  };

  const clearBulkAdds = () => setBulkRoutingGroups({ success: [], fail: [] });

  const getRoutingGroup = (routingGroupId: string) => {
    setAction('getRoutingGroup');
    makeRequest(`${BASE_URL}/${routingGroupId}`, 'get');
  };

  const getRoutingGroups = () => {
    setAction('getRoutingGroups');
    makeRequest(BASE_URL, 'get');
  };

  const updateRoutingGroup = (routingGroup: RoutingGroup) => {
    setAction('updateRoutingGroup');
    const body: ApiCreateRoutingGroup = sanitizeValue({
      Active: routingGroup.active,
      Description: routingGroup.description,
      MediaFirstID: routingGroup.mediafirstId,
    });

    makeRequest(`${BASE_URL}/${routingGroup.id}`, 'put', body);
  };

  const resetState = () => {
    setAction('');
    setBulkRoutingGroups({ success: [], fail: [] });
    setRoutingGroup(null);
    setRoutingGroups([]);
  };

  return {
    addRoutingGroup,
    routingGroup,
    routingGroups,
    bulkAddRoutingGroups,
    bulkRoutingGroups,
    clearBulkAdds,
    getRoutingGroup,
    getRoutingGroups,
    loading,
    updateRoutingGroup,
  };
};

const mapApiRoutingGroup = (
  routingGroup: CreateRoutingGroup
): ApiCreateRoutingGroup => ({
  Active: routingGroup.active,
  Description: routingGroup.description,
  MediaFirstID: routingGroup.mediafirstId,
});

const mapBulkResponse = (
  response: ApiBulkResponse<ApiRoutingGroup, ApiCreateRoutingGroup>
): BulkRoutingGroup => ({
  success: response.Success.map((routingGroup) =>
    mapRoutingGroup(routingGroup)
  ),
  fail: response.Fail.map((routingGroup) => ({
    active: routingGroup.Active,
    description: routingGroup.Description,
    mediafirstId: routingGroup.MediaFirstID,
  })),
});

export const mapRoutingGroup = (
  routingGroup: ApiRoutingGroup
): RoutingGroup => ({
  id: routingGroup.ID,
  active: routingGroup.Active,
  description: routingGroup.Description,
  mediafirstId: routingGroup.MediaFirstID,
});
